import React, { useState, createContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { clearSelectedDocument } from 'src/actions/documentActions';
import { DEFAULT_ROW_OPTIONS } from 'src/constants';

export const DashboardContext = createContext(null);

const DashboardContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.document.list);

  const [activeTab, setActiveTab] = useState(0);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('');
  const [checkedDocuments, setCheckedDocuments] = useState([]);
  const [docSearchFields, setDocSearchFields] = useState([]);
  const [rowsPerPageOptions, setRowsPerPageOptions] = useState(DEFAULT_ROW_OPTIONS);
  const [loadingFile, setLoadingFile] = useState(false);
  const [splitPDF, setSplitPDF] = useState();
  const [modifiedPDF, setModifiedPDF] = useState();

  // Checks whether the specified document has its checkbox selected.
  const isDocumentChecked = (docId) => checkedDocuments.includes(docId);

  // Toggles the checked state of the specified document.
  const toggleCheckState = (docId) => {
    const checked = [...checkedDocuments];
    const index = checked.indexOf(docId);
    if (index < 0) {
      checked.push(docId);
    } else {
      checked.splice(index, 1);
    }
    setCheckedDocuments(checked);
  };

  // Toggles the checked state of all documents.
  const toggleAllCheckStates = () => {
    setCheckedDocuments(checkedDocuments.length === 0 ? documents.map((doc) => doc.docId) : []);
  };

  // Finds a document given its docId
  const findDocument = (docId) => documents.find((doc) => doc.docId === docId);

  // Triggered when the search data has changed as the rexult of
  // a new search being performed. Loads the search results and
  // resets the browsing state.
  useEffect(() => {
    // Store a copy of the search results.
    setDocuments([...data]);
    dispatch(clearSelectedDocument());

    // Adjust the number of rows-per-page options based on the actual number of documents.
    const options = DEFAULT_ROW_OPTIONS.filter((val) => val <= data.length);
    setRowsPerPageOptions(data.length < 500 && !options.includes(data.length)
      ? [...options, data.length]
      : options);

    // Reset the browsing state.
    setOrder('');
    setOrderBy('');
    setCheckedDocuments([]);
  }, [data, dispatch]);

  return (
    <DashboardContext.Provider
      value={{
        activeTab,
        setActiveTab,
        searchPerformed,
        setSearchPerformed,
        documents,
        setDocuments,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        checkedDocuments,
        setCheckedDocuments,
        docSearchFields,
        setDocSearchFields,
        rowsPerPageOptions,
        isDocumentChecked,
        findDocument,
        toggleCheckState,
        toggleAllCheckStates,
        loadingFile,
        setLoadingFile,
        splitPDF,
        setSplitPDF,
        modifiedPDF,
        setModifiedPDF
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

DashboardContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default DashboardContextProvider;
